import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomAlert from '../CustomAlert';

const apiUrl = process.env.REACT_APP_API_URL;

const EMPTY = {
  name: '',
  price: '',
  cost_price: '',
  tax: '18',
  quantity: '',
  type: '',
  discount: '',
  discount_percentage: '',
  description: '',
  unit: 'пар',
  location: '',
  category_id: '',
  manufacturer_id: '',
};

/**
 * Product form. Renders no page chrome so it can sit either in the Drawer on
 * the list page or standalone on the /products/add route.
 *
 * onSuccess(created) — drawer closes and the list refreshes
 */
export default function AddProduct({ onSuccess }) {
  const navigate = useNavigate();
  const [product, setProduct] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    // Load categories
    fetch(`${apiUrl}/categories`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => res.json())
      .then(setCategories)
      .catch((err) => {
        console.error('Failed to load categories:', err);
        setAlert({ show: true, message: 'Неуспешно вчитување на категории', type: 'error' });
      });

    // Load manufacturers
    fetch(`${apiUrl}/manufacturer`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => res.json())
      .then(setManufacturers)
      .catch((err) => {
        console.error('Failed to load manufacturers:', err);
        setAlert({ show: true, message: 'Неуспешно вчитување на производители', type: 'error' });
      });
  }, []);

  const [showMore, setShowMore] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // const { name, value } = e.target;
    if (!product.name || !product.price || !product.tax) {
      setAlert({ show: true, message: 'Пополнете ги сите задолжителни полиња.', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/product-add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(product),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create product');
      }

      const data = await response.json();
      setProduct(EMPTY);

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate('/products/list');
      }
    } catch (error) {
      console.error(error);
      setAlert({ show: true, message: `Error: ${error.message}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Same field spec as AddPartner — the two sit side by side as sibling
  // drawers, so they should not look like different applications. Plain
  // functions, NOT components: a component declared inside render would
  // remount the input on every keystroke and drop focus.
  const LABEL = 'block text-sm font-medium text-gray-700';
  const INPUT =
    'mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600';

  const renderField = ({ label, name, type = 'text', required = false, span, placeholder }) => (
    <div key={name} className={span}>
      <label className={LABEL}>
        {label}
        {required && ' *'}
      </label>
      <input
        type={type}
        name={name}
        value={product[name]}
        onChange={handleChange}
        required={required}
        placeholder={placeholder}
        className={INPUT}
      />
    </div>
  );

  const renderSelect = ({ label, name, required = false, children }) => (
    <div key={name}>
      <label className={LABEL}>
        {label}
        {required && ' *'}
      </label>
      <select
        name={name}
        value={product[name]}
        onChange={handleChange}
        required={required}
        className={INPUT}
      >
        {children}
      </select>
    </div>
  );

  return (
    <>
      <CustomAlert
        isOpen={alert.show}
        type={alert.type}
        title={alert.type === 'success' ? 'Успешно' : 'Грешка'}
        message={alert.message}
        onClose={() => setAlert({ show: false, message: '', type: 'success' })}
      />
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Required fields */}
        <div className="border-l-4 border-green-500 pl-3">
          <h2 className="text-base font-semibold text-gray-800">Основни податоци</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
          {renderField({
            label: 'Назив на производ',
            name: 'name',
            required: true,
            span: 'sm:col-span-2 lg:col-span-3',
            placeholder: 'Внесете назив',
          })}
          {renderField({
            label: 'Продажна цена',
            name: 'price',
            type: 'number',
            required: true,
            placeholder: '0.00',
          })}
          {renderField({
            label: 'Набавна цена',
            name: 'cost_price',
            type: 'number',
            placeholder: '0.00',
          })}
          {renderSelect({
            label: 'Данок',
            name: 'tax',
            required: true,
            children: (
              <>
                <option value="0">0%</option>
                <option value="5">5%</option>
                <option value="18">18%</option>
              </>
            ),
          })}
          {renderSelect({
            label: 'Единица мерка',
            name: 'unit',
            children: (
              <>
                <option value="пар">пар</option>
                <option value="kg">kg</option>
                <option value="m">m</option>
              </>
            ),
          })}
          {renderSelect({
            label: 'Категорија',
            name: 'category_id',
            children: (
              <>
                <option value="">Изберете категорија</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </>
            ),
          })}
          {renderSelect({
            label: 'Производител',
            name: 'manufacturer_id',
            children: (
              <>
                <option value="">Изберете производител</option>
                {manufacturers.map((manufacturer) => (
                  <option key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.name}
                  </option>
                ))}
              </>
            ),
          })}
        </div>

        {/* Optional fields */}
        <div className="border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-semibold focus:outline-none transition-colors"
          >
            <span className="mr-2">{showMore ? '▼' : '▶'}</span>
            {showMore ? 'Сокриј дополнителни полиња' : 'Прикажи дополнителни полиња'}
          </button>

          {showMore && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 mt-3">
              {renderField({ label: 'Количина', name: 'quantity', type: 'number', placeholder: '0' })}
              {renderField({ label: 'Тип', name: 'type', placeholder: 'Тип на производ' })}
              {renderField({ label: 'Локација', name: 'location', placeholder: 'Локација во магацин' })}
              {renderField({ label: 'Попуст', name: 'discount', type: 'number', placeholder: '0.00' })}
              {renderField({
                label: 'Попуст %',
                name: 'discount_percentage',
                type: 'number',
                placeholder: '0',
              })}

              <div className="sm:col-span-2 lg:col-span-3">
                <label className={LABEL}>Опис</label>
                <textarea
                  name="description"
                  value={product.description}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Опис на производот..."
                  className={`${INPUT} resize-none`}
                />
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="w-full px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
          >
            {saving ? 'Се зачувува...' : 'Креирај производ'}
          </button>
        </div>
      </form>
    </>
  );
}
